import { openNotification } from 'components/notification';
import type { PromiseAllSettledResult } from 'interfaces/PromisedAllSettledResult';
import { PromiseAllSettledStatus } from 'interfaces/PromisedAllSettledResult';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { DocumentImageLibraryPayload, UploadDocumentImageResponse } from 'services/documents/DocumentRepository';
import { QueryKeys } from 'utils/queryKeys';
import { CheckImageExtension } from '../helpers/CheckImageExtension';
import { getImageStatusError } from '../helpers/getImageStatusError';
import type { ImageUploadErrorResponse } from '../shared/models/ImageUploadErrorResponse';
import { apiDocumentServiceRepository } from 'services/repositories/implementations/ApiDocumentServiceRepository';
import { trackError } from '@proposify/frontend-observability';

export function useImageUploadMutation() {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  const { mutate, isSuccess } = useMutation({
    mutationFn: async (payload: DocumentImageLibraryPayload) => {
      for (const i of payload.images) {
        if (!CheckImageExtension(i)) throw new Error('Unsupported image type');
      }

      try {
        return await apiDocumentServiceRepository.uploadDocumentLibraryImages({
          contentId: payload.contentId,
          images: payload.images,
        });
      } catch (error) {
        const e = new Error('Could not upload image to document service');
        trackError(e, {
          payload: JSON.stringify(payload),
          errorFrom: 'editor-frontend',
          cause: error,
        });

        throw e;
      }
    },
    onSuccess: async (result: PromiseAllSettledResult<UploadDocumentImageResponse>[], payload) => {
      await queryClient.invalidateQueries({ queryKey: QueryKeys.documentImageLibraryImages(payload.contentId) });

      const hasSuccess = result.some((r) => r.status === PromiseAllSettledStatus.Fulfilled);
      const errors = result.filter((r) => r.status === PromiseAllSettledStatus.Rejected);

      if (errors.length && hasSuccess) {
        openNotification({
          type: 'warning',
          title: t('document.image.errors.mixed_success_title'),
          description: t('document.image.errors.mixed_success_desc'),
        });
      } else if (hasSuccess) {
        if (payload.showSuccessNotification) {
          openNotification({
            type: 'success',
            title: t('document.image.success'),
            description: '',
          });
        }
      } else {
        const title =
          errors.length === 1 ? getImageStatusError(errors[0]?.reason?.status_code, t).title : t('document.image.errors.multiple_errors');

        const desc =
          errors.length === 1
            ? getImageStatusError(errors[0]?.reason?.status_code, t).desc
            : [...new Set(errors.map((e) => getImageStatusError(e?.reason?.status_code, t).desc))].join(', ');

        openNotification({
          title,
          description: desc,
          type: 'error',
        });
      }
    },
    onError: (error: Error) => {
      if (!error) return;

      if (error.message === 'Unsupported image type') {
        openNotification({
          title: t('document.image.errors.not_supported_types_title'),
          description: t('document.image.errors.not_supported_types_desc'),
          type: 'error',
        });
        return;
      }

      const { title, desc } = getImageStatusError((error as unknown as ImageUploadErrorResponse).status_code, t);
      openNotification({
        title,
        description: desc,
        type: 'error',
      });
    },
  });

  return {
    mutate,
    isSuccess,
    getUploadedImages: (data: PromiseAllSettledResult<UploadDocumentImageResponse>[]) => {
      return data?.map((r) => (r.status === PromiseAllSettledStatus.Fulfilled ? r.value : undefined)).filter((d) => d !== undefined) || [];
    },
  };
}
